import React, { useEffect, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable, DataTableSelectionMultipleChangeEvent } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Message } from 'primereact/message';
import { lagerCompaniesAPI, lagerFinishedProductsAPI, lagerLocationsAPI, lagerTransportAPI } from '../services/api';
import { LagerCompany, LagerFinishedProduct, LagerPlacement, LagerTransport, LagerUnit } from '../types';
import { showCustomAlert } from './CustomAlert';
import LagerPlacementPicker, { FormPlacement, PlacementRowOption } from './LagerPlacementPicker';

type TransportFormState = {
  productId: string;
  companyId: string;
  quantity: number;
  unit: LagerUnit;
  shippedDate: Date;
  squareMeters: number;
  shippedTo: string;
  notes: string;
  locationIds: string[];
  placements: FormPlacement[];
};

const unitOptions: Array<{ label: string; value: LagerUnit }> = [
  { label: 'kom', value: 'kom' },
  { label: 'paleta', value: 'paleta' },
  { label: 'm²', value: 'm2' }
];

function toDate(value: string | Date | undefined): Date {
  if (!value) return new Date();
  const d = typeof value === 'string' ? new Date(value) : value;
  return isNaN(d.getTime()) ? new Date() : d;
}

const LagerTransportPage: React.FC = () => {
  const [transports, setTransports] = useState<LagerTransport[]>([]);
  const [selectedTransports, setSelectedTransports] = useState<LagerTransport[]>([]);
  const [products, setProducts] = useState<LagerFinishedProduct[]>([]);
  const [companies, setCompanies] = useState<LagerCompany[]>([]);
  const [loading, setLoading] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editing, setEditing] = useState<LagerTransport | null>(null);
  const [error, setError] = useState('');
  const [stock, setStock] = useState<LagerPlacement[]>([]);
  const [stockLoading, setStockLoading] = useState(false);
  const [autoDistribute, setAutoDistribute] = useState(false);

  const [form, setForm] = useState<TransportFormState>({
    productId: '',
    companyId: '',
    quantity: 0,
    unit: 'paleta',
    shippedDate: new Date(),
    squareMeters: 0,
    shippedTo: '',
    notes: '',
    locationIds: [],
    placements: []
  });

  const dialogHeader = useMemo(() => (editing ? 'Izmeni transport' : 'Dodaj transport'), [editing]);

  const selectedProduct = useMemo(() => products.find((p) => p._id === form.productId), [products, form.productId]);

  const calculatedM2 = useMemo(() => {
    const qty = Number(form.quantity || 0);
    if (!selectedProduct || !qty) return 0;
    const m2PerPiece = Number(selectedProduct.m2PerPiece || 0);
    const perPallet = Number(selectedProduct.perPallet || 0);
    if (form.unit === 'm2') return qty;
    if (form.unit === 'kom') return qty * m2PerPiece;
    // paleta
    return qty * perPallet * m2PerPiece;
  }, [form.quantity, form.unit, selectedProduct]);

  useEffect(() => {
    setForm((p) => ({ ...p, squareMeters: Number(calculatedM2.toFixed(2)) }));
  }, [calculatedM2]);

  const neededPallets = useMemo(() => {
    const qty = Number(form.quantity || 0);
    if (!qty) return 0;
    if (form.unit === 'paleta') return Math.ceil(qty);
    const perPallet = Number(selectedProduct?.perPallet || 0);
    if (perPallet <= 0) return 0;
    if (form.unit === 'm2') {
      const m2PerPiece = Number(selectedProduct?.m2PerPiece || 0);
      return m2PerPiece > 0 ? Math.ceil(qty / m2PerPiece / perPallet) : 0;
    }
    return Math.ceil(qty / perPallet);
  }, [form.quantity, form.unit, selectedProduct]);

  // Gde se izabrani proizvod nalazi u skladištu (pri izmeni računa i palete koje je ovaj transport već uzeo)
  useEffect(() => {
    if (!dialogVisible || !form.productId) {
      setStock([]);
      return;
    }
    let cancelled = false;
    setStockLoading(true);
    lagerLocationsAPI
      .getProductStock(form.productId, editing?._id)
      .then((data) => {
        if (!cancelled) setStock(Array.isArray(data) ? data : []);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.response?.data?.message || err?.message || 'Greška pri učitavanju stanja u skladištu');
      })
      .finally(() => {
        if (!cancelled) setStockLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dialogVisible, form.productId, editing]);

  const stockRows = useMemo<PlacementRowOption[]>(
    () =>
      stock.map((s) => ({
        rowId: s.rowId,
        rowName: s.rowName || '',
        locationId: s.locationId,
        locationName: s.locationName || '',
        available: s.pallets
      })),
    [stock]
  );

  const stockById = useMemo(() => new Map(stockRows.map((r) => [r.rowId, r])), [stockRows]);
  const assignedPallets = form.placements.reduce((sum, p) => sum + Number(p.pallets || 0), 0);
  const totalInStorage = stockRows.reduce((sum, r) => sum + r.available, 0);

  // Stari transporti bez utovara mogu da se menjaju bez izbora redova
  const isLegacyEdit = !!editing && !(editing.placements || []).length;

  // Razlog zbog kog utovar ne može da se sačuva (prazno = ispravno)
  const placementError = useMemo(() => {
    if (!form.productId || !form.quantity) return '';
    if (stockLoading) return 'Učitavam stanje u skladištu...';
    // Proizvod koji nije smešten ni u jedan red (stara proizvodnja) se transportuje bez utovara
    if (totalInStorage === 0) return '';
    if (!form.placements.length) {
      if (isLegacyEdit) return '';
      return neededPallets > totalInStorage
        ? `U skladištu ima samo ${totalInStorage} paleta ovog proizvoda, a transport je ${neededPallets} paleta`
        : 'Izaberite redove iz kojih se utovaruje';
    }
    if (neededPallets > totalInStorage) {
      return `U skladištu ima samo ${totalInStorage} paleta ovog proizvoda, a transport je ${neededPallets} paleta`;
    }
    for (const pl of form.placements) {
      const info = stockById.get(pl.rowId);
      if (info && pl.pallets > info.available) {
        return `U redu "${info.locationName} / ${info.rowName}" ima ${info.available} paleta, a uneto je ${pl.pallets}`;
      }
    }
    if (assignedPallets < neededPallets) return `Utovareno ${assignedPallets} od ${neededPallets} paleta, izaberite još ${neededPallets - assignedPallets}`;
    if (assignedPallets > neededPallets) return `Utovareno ${assignedPallets}, a transport je ${neededPallets} paleta, smanjite za ${assignedPallets - neededPallets}`;
    return '';
  }, [form.productId, form.quantity, form.placements, stockLoading, totalInStorage, isLegacyEdit, neededPallets, stockById, assignedPallets]);

  const loadReferenceData = async () => {
    const [p, c] = await Promise.all([
      lagerFinishedProductsAPI.getAll(),
      lagerCompaniesAPI.getAll()
    ]);
    setProducts(Array.isArray(p) ? p : []);
    setCompanies(Array.isArray(c) ? c : []);
  };

  const loadTransports = async () => {
    const t = await lagerTransportAPI.getAll();
    setTransports(Array.isArray(t) ? t : []);
    setSelectedTransports([]);
  };

  const loadAll = async () => {
    setLoading(true);
    setError('');
    try {
      await Promise.all([loadTransports(), loadReferenceData()]);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Greška pri učitavanju transporta';
      setError(msg);
      showCustomAlert.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({
      productId: '',
      companyId: '',
      quantity: 0,
      unit: 'paleta',
      shippedDate: new Date(),
      squareMeters: 0,
      shippedTo: '',
      notes: '',
      locationIds: [],
      placements: []
    });
    setAutoDistribute(false);
    setError('');
    setDialogVisible(true);
  };

  const openEdit = (transport: LagerTransport) => {
    const productId =
      transport.originalProductId ||
      (typeof transport.productId === 'string' ? transport.productId : transport.productId?._id) ||
      '';
    const companyId = typeof transport.companyId === 'string' ? transport.companyId : transport.companyId?._id || '';
    setEditing(transport);
    setForm({
      productId,
      companyId,
      quantity: Number(transport.quantity || 0),
      unit: (transport.unit as LagerUnit) || 'paleta',
      shippedDate: toDate(transport.shippedDate),
      squareMeters: Number(transport.squareMeters || 0),
      shippedTo: transport.shippedTo || '',
      notes: transport.notes || '',
      locationIds: Array.from(new Set((transport.placements || []).map((p) => p.locationId))),
      placements: (transport.placements || []).map((p) => ({ rowId: p.rowId, pallets: Number(p.pallets || 0) }))
    });
    // Pri izmeni se zadržava sačuvani utovar dok se automatska raspodela ne uključi
    setAutoDistribute(false);
    setError('');
    setDialogVisible(true);
  };

  const handleSubmit = async () => {
    if (!form.productId) return setError('Proizvod je obavezan');
    if (!form.companyId) return setError('Firma je obavezna');
    if (!form.quantity || form.quantity <= 0) return setError('Količina mora biti veća od 0');
    if (placementError) return setError(placementError);
    try {
      setError('');
      const payload = {
        productId: form.productId,
        companyId: form.companyId,
        quantity: form.quantity,
        unit: form.unit,
        squareMeters: form.squareMeters,
        shippedDate: form.shippedDate.toISOString(),
        shippedTo: form.shippedTo,
        notes: form.notes,
        placements: form.placements.map((pl) => ({
          locationId: stockById.get(pl.rowId)?.locationId || '',
          rowId: pl.rowId,
          pallets: pl.pallets
        }))
      };
      if (editing?._id) {
        await lagerTransportAPI.update(editing._id, payload);
      } else {
        await lagerTransportAPI.create(payload);
      }
      setDialogVisible(false);
      await loadTransports();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Greška pri čuvanju transporta';
      setError(msg);
      showCustomAlert.error(msg);
    }
  };

  const handleDelete = (transport: LagerTransport) => {
    showCustomAlert.confirm(
      'Da li ste sigurni da želite da obrišete transport?',
      async () => {
        try {
          await lagerTransportAPI.delete(transport._id);
          await loadTransports();
        } catch (err: any) {
          const msg = err?.response?.data?.message || err?.message || 'Greška pri brisanju transporta';
          showCustomAlert.error(msg);
        }
      },
      'Potvrda brisanja'
    );
  };

  const handleBulkDelete = () => {
    const items = selectedTransports;
    if (!items.length) return;

    showCustomAlert.confirm(
      `Da li ste sigurni da želite da obrišete ${items.length} transport(a)?`,
      async () => {
        setBulkDeleting(true);
        try {
          const results = await Promise.allSettled(items.map((t) => lagerTransportAPI.delete(t._id)));
          const ok = results.filter((r) => r.status === 'fulfilled').length;
          const failed = results.length - ok;
          if (failed === 0) {
            showCustomAlert.success(`Obrisano: ${ok}`);
          } else {
            showCustomAlert.warning(`Obrisano: ${ok}, neuspešno: ${failed}`);
          }
          await loadTransports();
        } catch (err: any) {
          const msg = err?.response?.data?.message || err?.message || 'Greška pri brisanju transporta';
          showCustomAlert.error(msg);
        } finally {
          setSelectedTransports([]);
          setBulkDeleting(false);
        }
      },
      'Potvrda brisanja'
    );
  };

  const handleSelectAll = () => setSelectedTransports(transports);
  const handleClearSelection = () => setSelectedTransports([]);

  const dateBody = (row: LagerTransport) => {
    const d = new Date(row.shippedDate);
    return isNaN(d.getTime()) ? '' : d.toLocaleDateString('sr-RS');
  };

  const productBody = (row: LagerTransport) => {
    if (typeof row.productId !== 'string') return row.productId?.name || row.productName || '';
    return row.productName || '';
  };

  const companyBody = (row: LagerTransport) => {
    if (typeof row.companyId !== 'string') return row.companyId?.name || row.companyName || '';
    return row.companyName || '';
  };

  const unitBody = (row: LagerTransport) => (row.unit === 'm2' ? 'm²' : row.unit);

  const m2Body = (row: LagerTransport) => {
    const val = row.squareMeters ?? (row.unit === 'm2' ? row.quantity : 0);
    return typeof val === 'number' ? val.toFixed(2) : '';
  };

  const pickupBody = (row: LagerTransport) => (
    <div>
      {(row.placements || []).map((p) => (
        <div key={p.rowId}>
          {p.locationName} / {p.rowName} <span style={{ color: '#64748b' }}>({p.pallets} pal.)</span>
        </div>
      ))}
    </div>
  );

  const actionsBody = (row: LagerTransport) => (
    <div className="flex gap-2 justify-content-end">
      <Button icon="pi pi-pencil" className="action-icon-btn" text rounded onClick={() => openEdit(row)} tooltip="Izmeni" />
      <Button
        icon="pi pi-trash"
        className="action-icon-btn"
        text
        rounded
        severity="danger"
        onClick={() => handleDelete(row)}
        tooltip="Obriši"
      />
    </div>
  );

  return (
    <div style={{ padding: '1.5rem' }}>
      <Card title="Transport">
        {error && (
          <div style={{ marginBottom: '1rem' }}>
            <Message severity="error" text={error} />
          </div>
        )}

        <div className="flex justify-content-end gap-2" style={{ marginBottom: '1rem', flexWrap: 'wrap' }}>
          <Button
            icon="pi pi-check-square"
            label="Select all"
            severity="secondary"
            outlined
            onClick={handleSelectAll}
            disabled={!transports.length}
          />
          <Button
            icon="pi pi-times"
            label="Poništi"
            severity="secondary"
            outlined
            onClick={handleClearSelection}
            disabled={!selectedTransports.length}
          />
          <Button
            icon="pi pi-trash"
            label={`Obriši izabrane${selectedTransports.length ? ` (${selectedTransports.length})` : ''}`}
            severity="danger"
            outlined
            onClick={handleBulkDelete}
            disabled={!selectedTransports.length || bulkDeleting}
          />
          <Button icon="pi pi-plus" label="Dodaj" onClick={openCreate} />
          <Button icon="pi pi-refresh" label="Osveži" severity="secondary" outlined onClick={loadAll} />
        </div>

        <DataTable
          value={transports}
          loading={loading || bulkDeleting}
          paginator
          rows={10}
          rowsPerPageOptions={[10, 25, 50]}
          responsiveLayout="stack" breakpoint="768px"
          dataKey="_id"
          selectionMode="checkbox"
          selection={selectedTransports}
          onSelectionChange={(e: DataTableSelectionMultipleChangeEvent<LagerTransport[]>) => setSelectedTransports(e.value || [])}
        >
          <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
          <Column header="Datum" body={dateBody} sortable sortField="shippedDate" />
          <Column header="Proizvod" body={productBody} sortable />
          <Column header="Firma" body={companyBody} sortable />
          <Column field="quantity" header="Količina" />
          <Column header="Jedinica" body={unitBody} />
          <Column header="Kvadrati (m²)" body={m2Body} />
          <Column header="Utovareno iz" body={pickupBody} />
          <Column header="Akcije" body={actionsBody} style={{ width: '140px' }} />
        </DataTable>
      </Card>

      <Dialog
        header={dialogHeader}
        visible={dialogVisible}
        style={{ width: '720px', maxWidth: '96vw' }}
        onHide={() => setDialogVisible(false)}
        modal
        closable
      >
        <div className="p-fluid">
          <div className="grid">
            <div className="col-12">
              <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>Proizvod</label>
              <Dropdown
                value={form.productId}
                options={products}
                optionLabel="name"
                optionValue="_id"
                placeholder="Izaberite proizvod"
                filter
                showClear
                appendTo="self"
                onChange={(e) => setForm((p) => ({ ...p, productId: e.value || '', locationIds: [], placements: [] }))}
              />
            </div>

            <div className="col-12">
              <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>Firma</label>
              <Dropdown
                value={form.companyId}
                options={companies}
                optionLabel="name"
                optionValue="_id"
                placeholder="Izaberite firmu"
                filter
                showClear
                appendTo="self"
                onChange={(e) => setForm((p) => ({ ...p, companyId: e.value || '' }))}
              />
            </div>

            <div className="col-12 md:col-6">
              <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>Količina</label>
              <InputNumber
                value={form.quantity}
                onValueChange={(e) => setForm((p) => ({ ...p, quantity: Number(e.value || 0) }))}
                min={0}
                useGrouping={false}
              />
            </div>

            <div className="col-12 md:col-6">
              <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>Kvadrati (m²)</label>
              <InputText value={`${form.squareMeters || 0} m²`} disabled />
              <div className="p-message p-message-info" style={{ marginTop: '0.5rem', padding: '0.5rem 0.75rem' }}>
                <i className="pi pi-info-circle" style={{ marginRight: '0.5rem' }} />
                Kalkulisano: {calculatedM2.toFixed(2)} m²
              </div>
            </div>

            <div className="col-12">
              <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>Jedinica</label>
              <Dropdown
                value={form.unit}
                options={unitOptions}
                optionLabel="label"
                optionValue="value"
                appendTo="self"
                onChange={(e) => setForm((p) => ({ ...p, unit: e.value }))}
              />
            </div>

            <div className="col-12">
              <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>Datum</label>
              <Calendar
                value={form.shippedDate}
                onChange={(e) => setForm((p) => ({ ...p, shippedDate: (e.value as Date) || new Date() }))}
                dateFormat="dd.mm.yy"
                appendTo="self"
                showIcon
                style={{ width: '100%' }}
              />
            </div>

            {form.productId && (
              <>
                <div className="col-12">
                  <div style={{ fontWeight: 600, borderTop: '1px solid var(--surface-border, #e2e8f0)', paddingTop: '0.75rem' }}>
                    Utovar iz skladišta
                  </div>
                  <small style={{ color: '#64748b' }}>
                    {totalInStorage > 0
                      ? `U skladištu: ${totalInStorage} paleta ovog proizvoda · za transport: ${neededPallets} paleta`
                      : 'Ovaj proizvod trenutno nije smešten ni u jedan red skladišta.'}
                  </small>
                </div>
                {totalInStorage > 0 && (
                  <LagerPlacementPicker
                    rows={stockRows}
                    locationIds={form.locationIds}
                    placements={form.placements}
                    needed={neededPallets}
                    autoDistribute={autoDistribute}
                    availableLabel="na stanju"
                    title="Utovar paleta"
                    onLocationIdsChange={(locationIds) => setForm((p) => ({ ...p, locationIds }))}
                    onPlacementsChange={(placements) => setForm((p) => ({ ...p, placements }))}
                    onAutoDistributeChange={setAutoDistribute}
                  />
                )}
              </>
            )}
          </div>

          {(placementError || error) && (
            <div style={{ marginTop: '1rem' }}>
              <Message severity={error && !placementError ? 'error' : 'warn'} text={placementError || error} style={{ width: '100%', justifyContent: 'flex-start' }} />
            </div>
          )}

          <div className="flex gap-2 justify-content-end" style={{ marginTop: '1.25rem' }}>
            <Button label="Sačuvaj" icon="pi pi-check" onClick={handleSubmit} disabled={!!placementError} />
            <Button label="Otkaži" icon="pi pi-times" severity="secondary" outlined onClick={() => setDialogVisible(false)} />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default LagerTransportPage;


